import React, { useEffect, useMemo, useState } from "react";
import { Check, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { billingApi, TIER_ORDER, TIER_COPY } from "@/lib/providerApi";
import { useLocalPrices, annualOf } from "@/lib/localPrice";

const PARTNER = "partner";

/**
 * The single partner ladder (Growth / Pro / Max / Scale / Scale+ + Pay as you go) for every world.
 * Money comes from GET /api/plans; the client only ever names (lane, tier, seats). A £0 plan applies at
 * once; a paid one goes to Stripe Checkout and comes back to /billing-return.
 * `onApplied(result)` fires after a £0 plan is applied (or after we leave for Stripe, with {redirecting:true}).
 */
export function PlanPicker({ onApplied, compact = false }) {
  const { lp, note } = useLocalPrices();
  const { user, refresh } = useAuth();
  const [cat, setCat] = useState(undefined);
  const current = user?.plan;
  const [billing, setBilling] = useState(current?.billing_model === "payg" ? "payg" : "subscription");
  const [seats, setSeats] = useState({});
  const [worlds, setWorlds] = useState({});
  const [cmp, setCmp] = useState(null);
  const [busy, setBusy] = useState(null);
  const [period, setPeriod] = useState(current?.interval === "month" ? "month" : "year");

  useEffect(() => {
    billingApi.plans().then((d) => setCat(d?.catalogue || null)).catch(() => setCat(null));
    if (user?.plan) billingApi.compare().then(setCmp).catch(() => setCmp(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lane = cat?.[PARTNER] || cat?.services_education || null;
  const tiers = useMemo(() => {
    if (!lane) return [];
    return Object.entries(lane)
      .sort(([a], [b]) => TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b))
      .map(([key, e]) => ({ key, ...e, copy: TIER_COPY[key] || { features: [] } }));
  }, [lane]);
  const subs = tiers.filter((t) => t.billing_model !== "payg" && !t.copy.apply);
  const payg = tiers.find((t) => t.billing_model === "payg");

  const isCurrent = (t) => !!current && current.lane !== "enterprise" && current.tier === t.key && (t.billing_model === "payg") === (current.billing_model === "payg");

  const choose = async (t) => {
    const extra = t.extra_seat_price ? seats[t.key] || 0 : 0;
    setBusy(t.key);
    try {
      const xv = t.extra_vertical_price ? worlds[t.key] || 0 : 0;
      const res = await billingApi.checkout({ lane: PARTNER, tier: t.key, seats: extra, extra_verticals: xv, interval: period }, window.location.origin);
      if (!res.checkout_required) {
        await refresh();
        toast.success(`${t.tier_name} plan is active`);
        onApplied?.(res);
        return;
      }
      onApplied?.({ redirecting: true, session_id: res.session_id });
      window.location.assign(res.url);
    } catch (e) {
      toast.error(e.message || "Couldn't start checkout");
    } finally {
      setBusy(null);
    }
  };

  if (cat === undefined) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>;
  if (!lane) return <p className="text-sm text-destructive">Couldn't load the plan catalogue. Please refresh.</p>;

  return (
    <div data-testid="plan-picker">
      <div className="inline-flex rounded-full bg-muted p-1">
        {["subscription", "payg"].map((b) => (
          <button key={b} type="button" data-testid={`plan-billing-${b}`} onClick={() => setBilling(b)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${billing === b ? "bg-background shadow" : "text-muted-foreground"}`}>
            {b === "subscription" ? "Subscription · 0%" : "Pay as you go · 2.7%"}
          </button>
        ))}
      </div>
      {billing !== "payg" && (
        <div className="ml-2 mt-2 inline-flex rounded-full bg-muted p-1 align-top sm:mt-0" role="group" aria-label="Billing period">
          {[["year", "Yearly, 2 months on us"], ["month", "Monthly"]].map(([k, label]) => (
            <button key={k} type="button" data-testid={"plan-period-" + k} aria-pressed={period === k} onClick={() => setPeriod(k)} className={"rounded-full px-4 py-2 text-sm font-semibold transition-colors " + (period === k ? "bg-background shadow" : "text-muted-foreground")}>{label}</button>
          ))}
        </div>
      )}
      {cmp && cmp.message ? <p className="mt-3 rounded-xl bg-primary/10 px-3 py-2 text-sm" data-testid="plan-compare">{cmp.message}</p> : null}
      <p className="mt-3 text-sm text-muted-foreground">One plan for every world — Services, Education, Food, Casting, Experiences and Rentals. Rentals add a 5% owner rental-protection fee per digital booking on every plan.</p>

      {billing === "payg" && payg ? (
        <PlanCard t={payg} current={isCurrent(payg)} busy={busy === payg.key} onChoose={() => choose(payg)} price={lp(0)} />
      ) : (
        <div className={`mt-5 grid gap-4 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {subs.map((t) => {
            const extra = (t.extra_seat_price ? (seats[t.key] || 0) * t.extra_seat_price : 0) + (t.extra_vertical_price ? (worlds[t.key] || 0) * t.extra_vertical_price : 0);
            const total = Math.round((t.monthly + extra) * 100) / 100;
            return (
              <PlanCard key={t.key} t={t} current={isCurrent(t)} busy={busy === t.key} onChoose={() => choose(t)} price={period === "year" && annualOf(total) ? lp(annualOf(total)) : lp(total)} per={period === "year" && annualOf(total) ? "/yr" : "/mo"}>
                {t.extra_seat_price ? (
                  <div className="mt-3 flex items-center justify-between rounded-xl bg-muted/60 px-3 py-2 text-sm">
                    <span>Extra staff seats (+{lp(t.extra_seat_price)}/mo)</span>
                    <span className="inline-flex items-center gap-2">
                      <button type="button" aria-label="Fewer seats" className="rounded-full border border-border p-1" onClick={() => setSeats((s) => ({ ...s, [t.key]: Math.max(0, (s[t.key] || 0) - 1) }))}><Minus size={14} /></button>
                      <b className="w-4 text-center">{seats[t.key] || 0}</b>
                      <button type="button" aria-label="More seats" className="rounded-full border border-border p-1" onClick={() => setSeats((s) => ({ ...s, [t.key]: (s[t.key] || 0) + 1 }))}><Plus size={14} /></button>
                    </span>
                  </div>
                ) : null}
                  {t.extra_vertical_price ? (
                    <div className="mt-2 flex items-center justify-between rounded-xl bg-muted/60 px-3 py-2 text-sm">
                      <span>Extra worlds (+{lp(t.extra_vertical_price)}/mo each)</span>
                      <span className="inline-flex items-center gap-2">
                        <button type="button" aria-label="Fewer worlds" className="rounded-full border border-border p-1" onClick={() => setWorlds((s) => ({ ...s, [t.key]: Math.max(0, (s[t.key] || 0) - 1) }))}><Minus size={14} /></button>
                        <b className="w-4 text-center" data-testid={`worlds-${t.key}`}>{worlds[t.key] || 0}</b>
                        <button type="button" aria-label="More worlds" className="rounded-full border border-border p-1" onClick={() => setWorlds((s) => ({ ...s, [t.key]: Math.min(6 - (t.verticals_included || 0), (s[t.key] || 0) + 1) }))}><Plus size={14} /></button>
                      </span>
                    </div>
                  ) : null}
              </PlanCard>
            );
          })}
        </div>
      )}
      <p className="mt-4 text-xs text-muted-foreground">Partner plans are business subscriptions billed by Fable + to your business. Paid plans use secure Stripe checkout; change or cancel any time.{note ? " " + note : ""} Months with no bookings are free on every monthly plan, and each month we show you whether pay as you go would have cost less.</p>
    </div>
  );
}

function PlanCard({ t, current, busy, onChoose, price, per = " monthly", children }) {
  return (
    <div className={`relative mt-5 rounded-2xl border bg-card p-6 ${current ? "border-primary ring-1 ring-primary" : t.copy.highlight ? "border-primary/40" : "border-border"}`} data-testid={`plan-card-${t.key}`}>
      {t.copy.highlight && <span className="absolute -top-2.5 right-4 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold text-white">MOST POPULAR</span>}
      <h3 className="text-lg font-bold">{t.tier_name}</h3>
      <p className="text-xs text-muted-foreground">{t.copy.tagline}</p>
      <p className="mt-3 font-serif-display text-3xl font-bold">{price}<span className="ml-1 font-sans text-sm font-medium text-muted-foreground">{per}</span></p>
      <p className="mt-1 text-xs text-muted-foreground">
        {t.included_seats == null ? "Unlimited seats" : `${t.included_seats} seat included`} · {t.verticals_included == null ? "every world" : `${t.verticals_included} worlds`} · {t.listing_cap == null ? "unlimited listings" : `up to ${t.listing_cap} listings per world`}
      </p>
      {children}
      <ul className="mt-4 space-y-2 text-sm">
        {t.copy.features.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-primary" />{f}</li>)}
      </ul>
      <Button className="mt-5 w-full rounded-full" variant={current ? "outline" : "default"} disabled={current || busy} onClick={onChoose} data-testid={`plan-choose-${t.key}`}>
        {current ? "Current plan" : busy ? "Starting…" : t.monthly > 0 ? "Choose & pay" : "Choose plan"}
      </Button>
    </div>
  );
}
